import { useQuery, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

export const NO_SHOW_LABEL = 'no show'
export const RESCHEDULE_LABEL = 'reschedule'

const useLabels = (props = {}) => {
  const { appointment } = props

  // Fetch all labels
  const { data, isLoading, isError } = useQuery({
    queryKey: ['Labels'],
    queryFn: async () => {
      const res = await fetch(`${baseURL}/v2/rest/labels`)
      if (!res.ok) {
        throw new Error("We couldn't fetch the labels")
      }
      const result = await res.json()
      return result
    },
  })

  const labels = data || []
  const queryClient = useQueryClient()

  // Add or remove labels and optionally update reschedule status
  const handleUpdateLabels = async (labels, reschedule) => {
    try {
      if (!appointment || !appointment.id) {
        throw new Error('Appointment is required')
      }

      // Get the current appointment data
      const currentAppointment =
        queryClient.getQueryData(['Appointment', `${appointment.id}`]) ||
        appointment
      const currentLabelIds = currentAppointment.labels.map(label => label.id)
      const newLabelIds = labels.map(label => label.id)

      // Labels to add (in the new set but not in the current set)
      const labelsToAdd = labels.filter(
        label => !currentLabelIds.includes(label.id)
      )

      // Labels to remove (in the current set but not in the new set)
      const labelsToRemove = currentAppointment.labels.filter(
        label => !newLabelIds.includes(label.id)
      )

      // Add new labels
      for (const label of labelsToAdd) {
        await fetch(
          `${baseURL}/v2/rest/appointments/${appointment.id}/labels/${label.id}`,
          {
            method: 'PUT',
            headers,
          }
        )
      }

      // Remove old labels
      for (const label of labelsToRemove) {
        await fetch(
          `${baseURL}/v2/rest/appointments/${appointment.id}/labels/${label.id}`,
          {
            method: 'DELETE',
            headers,
          }
        )
      }

      // Update reschedule property if necessary
      if (reschedule !== undefined) {
        await fetch(`${baseURL}/v2/rest/appointments/${appointment.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            appointment: {
              reschedule,
            },
          }),
        })
      }

      // Refresh appointment data
      queryClient.invalidateQueries({
        queryKey: ['Appointment', `${appointment.id}`],
      })
    } catch (e) {
      throw Error(e)
    }
  }

  const addNoShowToAppointment = async () => {
    const noShowLabel = labels.find(label =>
      label.name.toLowerCase().includes(NO_SHOW_LABEL)
    )
    if (noShowLabel) {
      const labelsToUpdate = [...appointment.labels, noShowLabel]
      await handleUpdateLabels(labelsToUpdate)
    }
  }

  const removeNoShowFromAppointment = async () => {
    const noShowLabel = labels.find(label =>
      label.name.toLowerCase().includes(NO_SHOW_LABEL)
    )
    if (noShowLabel) {
      const labelsToUpdate = appointment.labels.filter(
        label => label.id !== noShowLabel.id
      )
      await handleUpdateLabels(labelsToUpdate)
    }
  }

  const addRescheduledToAppointment = async () => {
    const rescheduledLabel = labels.find(label =>
      label.name.toLowerCase().includes(RESCHEDULE_LABEL)
    )
    if (rescheduledLabel) {
      const labelsToUpdate = [...appointment.labels, rescheduledLabel]
      await handleUpdateLabels(labelsToUpdate, true)
    }
  }

  const removeRescheduledFromAppointment = async () => {
    const rescheduledLabel = labels.find(label =>
      label.name.toLowerCase().includes(RESCHEDULE_LABEL)
    )
    if (rescheduledLabel) {
      const labelsToUpdate = appointment.labels.filter(
        label => label.id !== rescheduledLabel.id
      )
      await handleUpdateLabels(labelsToUpdate, false)
    }
  }

  return {
    labels,
    loading: isLoading,
    error: isError,
    handleUpdateLabels,
    addNoShowToAppointment,
    removeNoShowFromAppointment,
    addRescheduledToAppointment,
    removeRescheduledFromAppointment,
  }
}

export default useLabels
